import { useMemo } from 'react';
import '../../styles/BrigadePlaceWork.css';
import Table from '../Table/Table';
import MyMap from '../Maps/MyMap';
import { defaultCellRender } from '../../utils/formatValueForHTML';

function BrigadePlaceWork({ currentSession, index, t }) {
  // Мемоизация списка маркеров
  const markers = useMemo(() => {
    // Проверяем, есть ли сигналы для обработки
    if (![currentSession?.signals?.[index]] || [currentSession?.signals?.[index]].length === 0) {
      return [];
    }

    return [currentSession?.signals?.[index]].flatMap((signal) => {
      const markers = [];

      // Функция для создания маркера
      const createMarker = (coords, type, popupContent) => ({
        position: [coords?.lat, coords.lon],
        type,
        popupContent,
      });

      // Проверка и создание маркеров для каждого типа координат
      if (signal?.iurCoords && signal?.iurCoords.lat && signal?.iurCoords.lon) {
        markers.push(createMarker(signal.iurCoords, 'green', t?.markerIur || 'ИУР'));
      }

      if (signal?.kuobzCoords && signal?.kuobzCoords.lat && signal?.kuobzCoords?.lon) {
        markers.push(createMarker(signal.kuobzCoords, 'blue', t?.markerKuobz || 'КУОБЗ'));
      }

      if (signal?.uippsCoords && signal?.uippsCoords?.lat && signal?.uippsCoords?.lon) {
        markers.push(createMarker(signal.uippsCoords, 'red', t?.markerUipps || 'УИППС'));
      }
      if (signal?.uippsCoords1 && signal?.uippsCoords1?.lat && signal?.uippsCoords1?.lon) {
        markers.push(createMarker(signal?.uippsCoords, 'red', t?.markerUipps || 'УИППС'));
      }

      return markers;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession?.signals?.[index], t]);

  // Колонки таблицы
  const columns = useMemo(
    () => [
      {
        Header: t?.tableHeaderRoad || 'Дорога',
        accessor: 'road',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.tableHeaderStation || 'Станция/Перегон',
        accessor: 'station',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.tableHeaderOrd || 'Ордината места работ',
        accessor: 'ord',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.tableHeaderObject || 'Объект',
        accessor: 'obj',
        Cell: ({ value }) => <span className="text-nowrap">{defaultCellRender(value)}</span>,
      },
      {
        Header: t?.tableHeaderRail || 'РЦ',
        accessor: 'railName',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.tableHeadeAdjacentRails || 'Смежные РЦ',
        accessor: 'adjacentRails',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.tableHeaderWorkMode || 'Режим',
        accessor: 'workMode',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.tableHeaderWarningDistance || 'Расстояние оповещ.',
        accessor: 'warningDistance',
        Cell: ({ value }) => defaultCellRender(value),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentSession, t]
  );

  const columnsTableCoords = useMemo(
    () => [
      {
        Header: () => <div style={{ color: '#33A74D' }}>{t?.iurCoordinates || 'Координаты ИУР'}</div>,
        accessor: 'iurCoords',
        Cell: ({ value }) => {
          // Проверяем, что значение является объектом с нужными свойствами
          if (value && value?.lat && value?.lon) {
            return (
              <div style={{ color: '#33A74D' }}>
                <div>{defaultCellRender(value?.lat)}</div>
                <div>{defaultCellRender(value?.lon)}</div>
              </div>
            );
          } else {
            return '-';
          }
        },
      },
      {
        Header: () => <div style={{ color: '#166EF1' }}>{t?.kuobzCoordinates || 'Координаты КУОБЗ'}</div>,
        accessor: 'kuobzCoords',
        Cell: ({ value }) => {
          // Проверяем, что значение является объектом с нужными свойствами
          if (value && value?.lat && value?.lon) {
            return (
              <div style={{ color: '#166EF1' }}>
                <div>{value?.lat}</div>
                <div>{value?.lon}</div>
              </div>
            );
          } else {
            return '-';
          }
        },
      },
      {
        Header: () => <div style={{ color: '#CA0000' }}>{`${t?.uippsCoordinates} 1` || 'Координаты УИППС 1'}</div>,
        accessor: 'uippsCoords',
        Cell: ({ value }) => {
          // Проверяем, что значение является объектом с нужными свойствами
          if (value && value?.lat && value?.lon) {
            return (
              <div style={{ color: '#CA0000' }}>
                <div>{value?.lat}</div>
                <div>{value?.lon}</div>
              </div>
            );
          } else {
            return '-';
          }
        },
      },
      {
        Header: () => <div style={{ color: '#CA0000' }}>{`${t?.uippsCoordinates} 2` || 'Координаты УИППС 2'}</div>,
        accessor: 'uippsCoords2',
        Cell: ({ value }) => {
          // Проверяем, что значение является объектом с нужными свойствами
          if (value && value?.lat && value?.lon) {
            return (
              <div style={{ color: '#CA0000' }}>
                <div>{value?.lat}</div>
                <div>{value?.lon}</div>
              </div>
            );
          } else {
            return '-';
          }
        },
      },
      {
        Header: () => <div style={{ color: '#CA0000' }}>{`${t?.uippsCoordinates} 3` || 'Координаты УИППС 3'}</div>,
        accessor: 'uippsCoords3',
        Cell: ({ value }) => {
          // Проверяем, что значение является объектом с нужными свойствами
          if (value && value?.lat && value?.lon) {
            return (
              <div style={{ color: '#CA0000' }}>
                <div>{value?.lat}</div>
                <div>{value?.lon}</div>
              </div>
            );
          } else {
            return '-';
          }
        },
      },

      {
        Header: () => <div style={{ color: '#CA0000' }}>{`${t?.uippsCoordinates} 4` || 'Координаты УИППС 4'}</div>,
        accessor: 'uippsCoords4',
        Cell: ({ value }) => {
          // Проверяем, что значение является объектом с нужными свойствами
          if (value && value?.lat && value?.lon) {
            return (
              <div style={{ color: '#CA0000' }}>
                <div>{value?.lat}</div>
                <div>{value?.lon}</div>
              </div>
            );
          } else {
            return '-';
          }
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentSession.signals, t]
  );

  return (
    <div className="brigade-place-work-table-container">
      {/* Таблица с данными о текущем месте работ */}
      <Table data={currentSession ? [currentSession] : []} columns={columns} hiddenDiv />
      {/*Таблица сигналов */}
      <Table
        className="customBorderRadius"
        data={currentSession?.signals?.[index] ? [currentSession?.signals?.[index]] : []}
        columns={columnsTableCoords}
        hiddenDiv
      />
      {/* Карта, требуется передавать координаты */}
      <div className="brigade-place-work-map-container">
        <MyMap markers={markers} t={t} />
      </div>
    </div>
  );
}

export default BrigadePlaceWork;
